import type { RouteRecordRaw } from 'vue-router';
import {
  auth,
  checkCustomUserQuestionnaire,
  checkCustomUserQuestionnaireWIthFacility,
  checkUserQuestionnaire,
  guest,
  isApproved,
} from '@/middleware';
import { routeDashboards, routeCompany } from './modules';
import dayjs from 'dayjs';
import ErrorPage from '@/pages/ErrorPage.vue';
import { TimeFormats } from '@/utils/helpers/dateTimeFormats';

const routes: RouteRecordRaw[] = [
  ...routeDashboards,
  {
    path: '/',
    name: 'home',
    redirect: { name: 'auth.login' },
  },
  {
    path: '/company/approved',
    name: 'company.approved',
    beforeEnter: [auth, isApproved],
    component: ErrorPage,
    meta: {
      error:
        'Thank you for your request to activate Kooling Reg-X! Your account will be verified and activated within 24 hours.',
    },
  },
  {
    path: '/company/:company/pscl-print',
    name: 'company.pscl-print',
    meta: {
      title: `Report-${dayjs().format(TimeFormats.print)}`,
    },
    component: () => import('@/pages/pscl/Print.vue'),
    beforeEnter: auth,
  },
  ...routeCompany,
  {
    path: '/q/:linkId',
    name: 'user.custom-questionnaire',
    component: () => import('@/pages/ErrorNotFound.vue'),
    beforeEnter: checkCustomUserQuestionnaire,
  },
  {
    path: '/qc/:companyToken',
    name: 'user.company.custom-questionnaire',
    beforeEnter: checkCustomUserQuestionnaireWIthFacility,
    component: () =>
      import(
        '@/pages/CompanyForm/CreateQuestionnaireLinkWithSelectFacility.vue'
      ),
  },
  {
    path: '/user-questionnaire',
    component: () => import('@/layouts/PublicLayout.vue'),
    meta: {
      showLocale: true,
    },
    children: [
      {
        name: 'user.questionnaire',
        path: '',
        beforeEnter: checkUserQuestionnaire,
        component: () => import('@/pages/CompanyForm/UserQuestionnaire.vue'),
      },
      {
        name: 'user.questionnaire.success',
        path: 'success',
        component: () => import('@/pages/CompanyForm/PublicSuccess.vue'),
      },
    ],
  },
  {
    path: '/error',
    name: 'error',
    component: ErrorPage,
  },
  {
    path: '/register',
    beforeEnter: guest,
    meta: {
      title: 'Register',
    },
    children: [
      {
        path: '',
        component: () => import('@/pages/auth/CompanyRegister.vue'),
        name: 'auth.register',
      },
      {
        path: ':city',
        component: () => import('@/pages/auth/CompanyRegister.vue'),
        name: 'auth.register.city',
      },
    ],
  },
  {
    path: '/login',
    beforeEnter: guest,
    meta: {
      title: 'Login',
    },
    children: [
      {
        path: '',
        name: 'auth.login',
        component: () => import('@/pages/auth/LoginPage.vue'),
      },
      {
        path: ':city',
        name: 'auth.login.city',
        component: () => import('@/pages/auth/LoginPage.vue'),
      },
    ],
  },
  {
    path: '/reset-password',
    component: () => import('@/layouts/PublicLayout.vue'),
    beforeEnter: guest,
    children: [
      {
        path: '',
        name: 'auth.reset',
        component: () => import('@/pages/auth/ResetPassword.vue'),
      },
    ],
  },
  {
    path: '/terms-and-conditions',
    component: () => import('@/layouts/DefaultLayout.vue'),
    children: [
      {
        path: '',
        name: 'terms-and-conditions',
        component: () => import('@/pages/TermsAndConditions.vue'),
        meta: {
          title: 'Terms And Conditions',
        },
      },
    ],
  },
  {
    path: '/privacy-policy',
    component: () => import('@/layouts/DefaultLayout.vue'),
    children: [
      {
        path: '',
        name: 'privacy-policy',
        component: () => import('@/pages/PrivacyPolicy.vue'),
        meta: {
          title: 'Privacy Policy',
        },
      },
      {
        path: 'survey',
        name: 'privacy-policy-survey',
        component: () => import('@/pages/PrivacyPolicySurvey.vue'),
        meta: {
          title: 'Privacy Policy',
        },
      },
    ],
  },
  {
    path: '/data-processing-addendum',
    component: () => import('@/layouts/DefaultLayout.vue'),
    children: [
      {
        path: '',
        name: 'data-processing-addendum',
        component: () => import('@/pages/DataProcessingAddendum.vue'),
        meta: {
          title: 'Data Processing Addendum',
        },
      },
    ],
  },
  {
    path: '/:catchAll(.*)*',
    component: () => import('@/pages/ErrorNotFound.vue'),
  },
];

export default routes;
